import { RefObject, useLayoutEffect, useRef, useState } from 'react'

import { canUseWindow } from '@library/environment'

export type RevealTrigger = 'scroll' | 'load'

// Trigger config for the scroll reveal, taken verbatim from the CwC prototype's `useReveal`
// (cinematic.jsx): fire when ~15% of the element has crossed in, nudged just before its bottom edge.
const REVEAL_THRESHOLD = 0.15
const REVEAL_ROOT_MARGIN = '0px 0px -5% 0px'

function prefersReducedMotion(): boolean {
  return (
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  )
}

/**
 * Single-shot reveal state for the canonical Appearance animation. Returns a `ref` to attach to the
 * element and an `isRevealed` flag that drives the fade+rise.
 *
 * No-JS safety: a scroll-triggered element renders visible in SSR and is only hidden later, on the
 * client, inside the layout effect — so a no-JS visitor keeps fully-visible content and the server
 * HTML matches the client's first render (no hydration mismatch). A no-`IntersectionObserver` or
 * `prefers-reduced-motion: reduce` client also leaves it revealed.
 *
 * - `trigger: 'scroll'` (default): the element is NOT pre-hidden on mount. It is observed, and the
 *   IntersectionObserver's own callback — which runs after layout has settled and re-evaluates on
 *   layout shifts (images loading, etc.) — decides. It hides the element only when the element is
 *   **entirely below the viewport** (so hiding is never visible), then reveals it once
 *   (single-shot-then-disconnect) when it scrolls into view. Content that is on screen at mount
 *   (tall viewport, scroll restoration, slow hydration after the SSR page has already painted) is
 *   never moved to hidden, so it never flashes visible→hidden→in. This replaces an earlier version
 *   that hid every scroll element unconditionally (which flashed on-screen content) and, before
 *   that, one that measured position synchronously at mount (which was layout-timing fragile and
 *   made reveals non-uniform). Using the observer's post-layout measurement avoids both.
 * - `trigger: 'load'`: reveal on mount (for above-the-fold elements, e.g. the Hero badge, where
 *   there is nothing to scroll to). Starts hidden in SSR so the reveal genuinely animates.
 */
export function useReveal<T extends HTMLElement = HTMLDivElement>({
  trigger = 'scroll',
}: { trigger?: RevealTrigger } = {}): {
  ref: RefObject<T>
  isRevealed: boolean
  immediate: boolean
} {
  const ref = useRef<T>(null)
  // Initial (SSR + first client render) state per trigger:
  // - scroll: revealed (visible). The element is only hidden later, on the client, and only once the
  //   observer confirms it is entirely below the viewport — so no-JS keeps content visible, there is
  //   no hydration mismatch, and on-screen content is never hidden (no flash).
  // - load: hidden. A load-triggered element is above the fold, so if it rendered visible the
  //   client-side hide+reveal would collapse into a single frame and the entrance would never play.
  //   Starting hidden means the reveal-on-mount genuinely animates 0→1. Trade-off: a no-JS visitor
  //   does not see a load-triggered element — acceptable only for decorative ones (e.g. the Hero
  //   badge, which is aria-hidden with its meaning carried by separate visually-hidden text).
  const [isRevealed, setIsRevealed] = useState(trigger !== 'load')
  // `immediate` skips the transition (used to show the end state at once under reduced motion).
  const [immediate, setImmediate] = useState(false)

  useLayoutEffect(() => {
    if (!canUseWindow()) return undefined

    const node = ref.current
    if (!node) return undefined

    if (prefersReducedMotion()) {
      // Show the end state immediately, with no animation. For load this reveals the (SSR-hidden)
      // element; for scroll it is already revealed, so this is a no-op beyond disabling animation.
      setImmediate(true)
      setIsRevealed(true)
      return undefined
    }

    if (trigger === 'load') {
      // Reveal on the next frame so the transition runs from the SSR-hidden state (0 → 1). The load
      // trigger does not use IntersectionObserver, so it is not gated on IO support.
      const raf = window.requestAnimationFrame(() => setIsRevealed(true))
      return () => window.cancelAnimationFrame(raf)
    }

    // Scroll trigger needs IntersectionObserver; without it, leave the element revealed.
    if (typeof window.IntersectionObserver === 'undefined') return undefined

    // Do NOT pre-hide. The element stays in its revealed (SSR) state until the observer — which
    // measures after layout has settled — decides. This never hides content that is already on
    // screen (hydration after paint, scroll restoration, tall viewports), so there is no flash.
    const observer = new window.IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsRevealed(true)
            observer.disconnect()
            return
          }
          // Not yet at the reveal threshold. Hide the element ONLY if it is entirely below the
          // viewport, so hiding is invisible and it can animate in when scrolled to. Content that is
          // on screen, straddling the fold, or scrolled above stays revealed (no flash). `top` is
          // measured by the observer (layout-settled), so this is robust to hydration/image timing.
          if (entry.boundingClientRect.top >= window.innerHeight) {
            setIsRevealed(false)
          }
        })
      },
      { threshold: REVEAL_THRESHOLD, rootMargin: REVEAL_ROOT_MARGIN },
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [trigger])

  return { ref, isRevealed, immediate }
}
